// Passwordless sign-in: e-mail → 6-digit code (docs/project.md §4; Auth-Email / Auth-Code boards).
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { z } from 'zod';
import { LOGIN_CODE_LENGTH } from '@solungo/core/schema';
import { api } from '@/api/endpoints';
import { ApiRequestError } from '@/api/client';
import { setUser } from '@/state/session';
import { storage } from '@/lib/storage';
import { ErrorText, PrimaryButton } from '@/components/ui';
import { AuthFrame } from '@/components/AuthFrame';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';

const searchSchema = z.object({
  locale: z.string().optional(),
  target: z.string().optional(),
  share: z.string().optional(),
});

export const Route = createFileRoute('/auth')({
  validateSearch: searchSchema,
  component: AuthScreen,
});

const RESEND_SECONDS = 60;

const errorCode = (e: unknown): string => (e instanceof ApiRequestError ? e.code : 'unknown');

function AuthScreen() {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (search.locale) storage.setPendingLocale(search.locale);
    if (search.target) storage.setPendingTarget(search.target);
  }, [search.locale, search.target]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const errorMessage = (e: unknown): string => {
    if (e instanceof ApiRequestError) {
      if (e.code === 'invalid_code' || e.code === 'code_expired' || e.code === 'rate_limited' || e.code === 'validation') return t(`errors.${e.code}`);
      if (e.isNetwork) return tc('errorNetwork');
    }
    return tc('errorGeneric');
  };

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.auth.code(email);
      track('sign_in_code_requested', { resend: step === 'code', email });
      setStep('code');
      setCode('');
      setCooldown(RESEND_SECONDS);
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (e) {
      reportError(e);
      track('sign_in_failed', { step: 'email', reason: errorCode(e), email });
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value = code) => {
    if (value.length !== LOGIN_CODE_LENGTH || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.verify({ email, code: value, shareToken: search.share });
      setUser(res.user);
      track('signed_in', { new_user: res.isNew, via_invite: !!search.share, email: res.user.email });
      if (search.share) {
        void navigate({ to: '/share/$token', params: { token: search.share }, replace: true });
      } else if (res.onboardingRequired || res.user.name === null) {
        void navigate({ to: '/onboarding', replace: true });
      } else {
        void navigate({ to: '/', replace: true });
      }
    } catch (e) {
      reportError(e);
      track('sign_in_failed', { step: 'code', reason: errorCode(e), email });
      setError(errorMessage(e));
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const mm = String(Math.floor(cooldown / 60));
  const ss = String(cooldown % 60).padStart(2, '0');

  return (
    <AuthFrame
      brand={step === 'email'}
      onBack={step === 'code' ? () => setStep('email') : undefined}
      title={step === 'email' ? t('title') : t('codeTitle')}
      subtitle={step === 'email' ? t('subtitle') : t.rich('codeSentTo', { email, b: (c) => <strong className="font-semibold text-ink">{c}</strong> })}
    >
      {step === 'email' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void requestCode();
          }}
        >
          <label className="flex flex-col gap-1.5">
            <span className="field-label">{t('email')}</span>
            <input
              type="email"
              required
              autoFocus
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('emailPlaceholder')}
              data-testid="email-input"
              className="field"
            />
          </label>
          <PrimaryButton busy={busy} data-testid="get-code">
            {t('getCode')}
          </PrimaryButton>
          <ErrorText>{error}</ErrorText>
        </form>
      ) : (
        <form
          className="flex flex-col gap-5 desktop:gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          {/* The session recording shows a placeholder here: the code signs into the account */}
          <div
            role="group"
            aria-label={t('codeLabel')}
            className="relative flex gap-2"
            onClick={() => codeRef.current?.focus()}
            data-openpanel-replay-block
          >
            {Array.from({ length: LOGIN_CODE_LENGTH }).map((_, i) => {
              const active = i === Math.min(code.length, LOGIN_CODE_LENGTH - 1);
              return (
                <div
                  key={i}
                  className={`flex h-[58px] w-12 items-center justify-center rounded-field bg-surface text-[26px] font-semibold tabular-nums desktop:h-[52px] desktop:w-11 desktop:rounded-[9px] desktop:border desktop:text-[22px] ${
                    active ? 'shadow-[0_0_0_2px_var(--color-accent)] desktop:border-transparent' : 'desktop:border-border'
                  }`}
                >
                  {code[i] ?? ''}
                </div>
              );
            })}
            <input
              ref={codeRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={LOGIN_CODE_LENGTH}
              value={code}
              aria-label={t('codeLabel')}
              data-testid="code-input"
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, LOGIN_CODE_LENGTH);
                setCode(v);
                if (v.length === LOGIN_CODE_LENGTH) void verify(v);
              }}
              className="absolute inset-0 h-full w-full opacity-0"
            />
          </div>
          <PrimaryButton busy={busy} disabled={code.length !== LOGIN_CODE_LENGTH} data-testid="sign-in">
            {t('signIn')}
          </PrimaryButton>
          <ErrorText>{error}</ErrorText>
          <div className="flex flex-col gap-3.5 text-[15px] desktop:flex-row desktop:items-center desktop:justify-between desktop:text-[13px]">
            {cooldown > 0 ? (
              <span className="text-ink-tertiary">{t('resendIn', { time: `${mm}:${ss}` })}</span>
            ) : (
              <button type="button" className="text-left text-accent" onClick={() => void requestCode()}>
                {t('resend')}
              </button>
            )}
            <button type="button" className="text-left text-accent" onClick={() => setStep('email')}>
              {t('changeEmail')}
            </button>
          </div>
        </form>
      )}
      {step === 'email' && (
        <p className="m-0 text-[13px] leading-[18px] text-ink-secondary desktop:text-[12px] desktop:leading-[17px]">
          {t.rich('legal', {
            terms: (c) => (
              <a href="https://solungo.com/terms" className="text-accent" target="_blank" rel="noreferrer">
                {c}
              </a>
            ),
            privacy: (c) => (
              <a href="https://solungo.com/privacy" className="text-accent" target="_blank" rel="noreferrer">
                {c}
              </a>
            ),
          })}
        </p>
      )}
    </AuthFrame>
  );
}
